import React, { ReactElement, useEffect, useState } from 'react'

import { PagedListRequest } from 'domain/types/global/pagedListRequest';

import { useTranslation } from 'react-i18next';
import { RootStateCampanha } from '../../../../store/campanhaReducer';
import moment from 'moment';
import 'moment/dist/locale/pt-br'

import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { LotePedido, ResetLotePedido } from '../../../../store/pedidoLotes/actions/pedidoLotesAction';
import { LotePedidoResponse } from '../../../../types/lotePedido/lotePedidoResponse';
import { LOTEPEDIDO_GET } from '../../../../types/lotePedido/lotePedido';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Box,
  Typography,
  CircularProgress,
  SelectChangeEvent
} from '@mui/material';
import { Icon } from '@iconify/react';
import { formatDate } from 'base/src/utils/stringUtil';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { Tag } from 'base/src/components/Tag';
import { Button } from 'base/src/components/Button';
import NoData from 'base/src/components/NoData';
import { Modal, ModalContent } from 'base/src/components/Modal';
import { LoteActionsPopover } from './LoteActionsPopover';
import ModalRealizarPagamento from '../ModalRealizarPagamento/ModalRealizarPagamento';
import ModalDeletarPagamento from '../ModalDeletarPagamento/ModalDeletarPagamento';
import PagedLoteItemDetalhe from '../PagedLoteItemDetalhe/PagedLoteItemDetalhe';
import FilterDrawer from './components/filterDrawer/filterDrawer';
import { CampanhaPedidoResgateResponse } from '../../../../types/campanha/campanhaPedidoResgateResponse';
import { ObjectControl } from 'domain/utils/ReactUtils';
import ModalAdicionarSaldo from '../ModalAdicionarSaldo/ModalAdicionarSaldo';
import ModalNotaRepasse from '../ModalNotaRepasse';

const connector = connectUtil(
  (state: RootStateCampanha) => ({
    LotePedidoState: state.PedidoLotesReducer.LotePedido,
    CampanhasDisponiveis: state.CampanhasReducer?.campanhasAll,
    mudancaStatus: state.PedidoLotesReducer.mudancaStatus,
    loading: state.BaseReducer.Loading,
    CarteiraUsuarioInfo: state.CarteiraBaseReducer.CarteiraUsuario,
  }),
  { LotePedido, ResetLotePedido }
);

export interface PagedResgatesProps extends PropsFromRedux<typeof connector> {
  take: number;
  campanhaId: number;
  action?: React.ReactElement;
  navigatePagarPedidoButton?: ReactElement;
  openModalLote?: ReactElement;
}


function PagedAprovar(props: PagedResgatesProps) {
  const [request, setRequest] = useState<PagedListRequest<LotePedidoResponse>>({
    skip: 0,
    take: props.take,
    search: '',
    filter: [],
    data: {} as LotePedidoResponse
  });

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [isAnyPopoverOpen, setIsAnyPopoverOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<LotePedidoResponse | null>(null);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const modalRequest = {} as CampanhaPedidoResgateResponse;
  const modalControl = new ObjectControl(modalRequest, () => { });

  const { t } = useTranslation();

  moment.locale("pt-br");

  // Status options
  const statusOptions = [
    { id: '1', nome: 'Pendente' },
    { id: '2', nome: 'Processando' },
    { id: '3', nome: 'Pago' },
    { id: '4', nome: 'Excluido' },
    { id: '5', nome: 'Parcialmente Pago' }
  ];

  // Handle pagination change
  function handlePageChange(_: unknown, newPage: number) {
    const newRequest = {
      ...request,
      skip: newPage * request.take
    };
    setRequest(newRequest);

    // Reset data to clear previous page data
    props.ResetLotePedido(newRequest, props.take);

    // Load new page data
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  // Handle rows per page change
  function handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newTake = parseInt(event.target.value, 10);
    const newRequest = {
      ...request,
      take: newTake,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous data
    props.ResetLotePedido(newRequest, newTake);

    // Load new data
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  // Handle search
  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    const searchVal = event.target.value;
    setSearchValue(searchVal);

    const newRequest = {
      ...request,
      search: searchVal,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous results
    props.ResetLotePedido(newRequest, props.take);

    // Load new filtered data
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  function handleStatusFilterChange(event: SelectChangeEvent<string[]>) {
    const values = event.target.value as string[];
    setStatusFilter(values);

    const filterArray = values.length > 0 ? [{
      Values: values,
      Id: 0,
      Name: "Status"
    }] : [];

    const newRequest = {
      ...request,
      filter: filterArray,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetLotePedido(newRequest, props.take);

    props.LotePedido([props.campanhaId], newRequest, t);
  }

  function handleOpenDrawer() {
    setIsDrawerOpen(true);
  }

  function handleCloseDrawer() {
    setIsDrawerOpen(false);
  }

  // Handle apply filters (close drawer after applying)
  function handleApplyFilters() {
    setIsDrawerOpen(false);
  }

  function handleClearFilters() {
    setSearchValue('');
    setStatusFilter([]);

    // Reset request to initial state
    const newRequest = {
      ...request,
      search: '',
      filter: [],
      skip: 0
    };
    setRequest(newRequest);

    // Reset data and load fresh data without filters
    props.ResetLotePedido(newRequest, props.take);
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  // Format pagination label
  function formatPaginationLabel({ from, to, count }: { from: number; to: number; count: number }) {
    return `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`;
  }

  function handleModalClose() {
    // Fecha o modal
    setOpenModal(null);
    setSelectedItem(null);

    props.ResetLotePedido(request, props.take);
    props.LotePedido([props.campanhaId], request, t);

    window.dispatchEvent(new CustomEvent('reloadLoteList'));
  }

  function handlePopoverAction(action: string, item: LotePedidoResponse) {
    setSelectedItem(item);
    setOpenModal(action);
  }

  function handleModalOpenChange(open: boolean) {
    if (!open) {
      setOpenModal(null);
      setSelectedItem(null);

      // Refresh data after modal closes to ensure we have the latest status
      props.ResetLotePedido(request, props.take);
      props.LotePedido([props.campanhaId], request, t);
    }
  }

  function handleModalCancel() {
    handleModalOpenChange(false);
  }

  // Reset data when status changes and reload fresh data
  useEffect(() => {
    if (props.mudancaStatus && Object.keys(props.mudancaStatus).length > 0) {
      // Reset data first
      props.ResetLotePedido(request, props.take);

      // Then reload with fresh data
      props.LotePedido([props.campanhaId], request, t);
    }
  }, [props.mudancaStatus]);

  useEffect(() => {
    // Adiciona listener para o evento customizado de reload
    function handleReloadLoteList() {
      props.LotePedido([props.campanhaId], request, t);
    }

    window.addEventListener('reloadLoteList', handleReloadLoteList);

    // Cleanup do listener
    return () => {
      window.removeEventListener('reloadLoteList', handleReloadLoteList);
    };
  }, [request, props.take, props.campanhaId, t]);

  // Load initial data
  useEffect(() => {
    props.LotePedido([props.campanhaId], request, t);
  }, [props.campanhaId]);

  // Get data from state
  const data = props.LotePedidoState?.Result || [];
  const totalCount = props.LotePedidoState?.TotalCount || 0;
  const isLoading = props.loading.some(l => l.Name === LOTEPEDIDO_GET);

  const currentPage = Math.floor(request.skip / request.take);

  return (
    <Box sx={{
      '& @keyframes fadeIn': {
        '0%': {
          opacity: 0,
          transform: 'translateY(10px)'
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0)'
        }
      }
    }}>
      {/* Action Bar: Botão Voltar - SearchBar - Filtros - Gerar Lote */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}>
        {/* Left Side: Botão Voltar + Search Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {/* Botão Voltar */}
          {props.navigatePagarPedidoButton && (
            <Box sx={{
              '& button': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '& .MuiButton-startIcon': {
                  marginRight: 0.5
                }
              }
            }}>
              {/* Clonamos o botão para adicionar o ícone */}
              {React.cloneElement(props.navigatePagarPedidoButton as React.ReactElement, {
                startIcon: <Icon icon="mdi:arrow-left" />
              })}
            </Box>
          )}

          {/* Search Bar */}
          <TextField
            label="Pesquisar lotes"
            placeholder="Digite o nome do lote"
            value={searchValue}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 300,
              flex: 1,
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <Icon
                  icon="mdi:magnify"
                  style={{
                    marginRight: 8,
                    color: '#9CA3AF',
                    width: 20,
                    height: 20
                  }}
                />
              ),
            }}
          />
        </Box>

        {/* Right Side: Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            size="md"
            variant="secondary"
            iconName="mdi:filter-variant"
            onClick={handleOpenDrawer}
          >
            Filtros
          </Button>

          {props.openModalLote && (
            <Box>
              {props.openModalLote}
            </Box>
          )}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Lote</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Campanha</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Data</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Pedidos</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'grey.700' }}>Valor</TableCell>
              <TableCell width={50} sx={{ fontWeight: 600, color: 'grey.700' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8, border: 'none' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography variant="body2" color="text.secondary">
                      Carregando dados...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, border: 'none' }}>
                  <NoData />
                </TableCell>
              </TableRow>
            ) : (
              data.map((item, index) => {
                return (
                  <TableRow
                    key={item.CampanhaPedidoResgatePagamentoID || index}
                    hover
                    sx={{
                      '&:hover': !isAnyPopoverOpen ? {
                        bgcolor: 'action.hover',
                        cursor: 'pointer',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      } : {},
                      '&:nth-of-type(even)': {
                        bgcolor: 'grey.25'
                      },
                      transition: !isAnyPopoverOpen ? 'all 0.2s ease-in-out' : 'none',
                      animation: 'fadeIn 0.3s ease-in-out'
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                        {item.Nome}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {item.CampanhaID}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {formatDate(item.RealizadoEm)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {item.CampanhaPedidoResgatePagamentoStatusID && (
                        <Tag

                          variant={
                            item.CampanhaPedidoResgatePagamentoStatusID === 1
                              ? 'warning'
                              : item.CampanhaPedidoResgatePagamentoStatusID === 2
                                ? 'primary'
                                : item.CampanhaPedidoResgatePagamentoStatusID === 3
                                  ? 'positive'
                                  : item.CampanhaPedidoResgatePagamentoStatusID === 4
                                    ? 'danger'
                                    : item.CampanhaPedidoResgatePagamentoStatusID === 5
                                      ? 'neutral'
                                      : 'neutral'
                          }
                        >
                          {item.CampanhaPedidoResgatePagamentoStatusID === 1
                            ? 'Pendente'
                            : item.CampanhaPedidoResgatePagamentoStatusID === 2
                              ? 'Processando'
                              : item.CampanhaPedidoResgatePagamentoStatusID === 3
                                ? 'Pago'
                                : item.CampanhaPedidoResgatePagamentoStatusID === 4
                                  ? 'Excluido'
                                  : item.CampanhaPedidoResgatePagamentoStatusID === 5
                                    ? 'Parcialmente Pago'
                                    : item.CampanhaPedidoResgatePagamentoStatusID}
                        </Tag>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {item.QtdeUsuarios} pedidos
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'success.main',
                            fontWeight: 700,
                            fontSize: '0.95rem'
                          }}
                        >
                          {formatToBRL(item.ValorTotal)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Valor total
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <LoteActionsPopover
                          item={item}
                          campanhaId={props.campanhaId}
                          onAction={handlePopoverAction}
                          onPopoverOpenChange={setIsAnyPopoverOpen}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box sx={{ backgroundColor: 'grey.25', borderTop: '1px solid', borderColor: 'grey.200' }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPage={request.take}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={formatPaginationLabel}
            sx={{
              '& .MuiTablePagination-toolbar': {
                paddingX: 3,
                paddingY: 2
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: 'grey.700',
                fontWeight: 500
              },
              '& .MuiTablePagination-actions button': {
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50'
                }
              }
            }}
          />
        </Box>
      </TableContainer>

      {/* Modals */}
      {selectedItem && (
        <>
          {/* Modal Adicionar Saldo */}
          <Modal open={openModal === 'adicionar-saldo'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <ModalAdicionarSaldo
                ValorTotal={selectedItem.ValorTotal}
                campanhaPedidoResgatePagamentoId={selectedItem.CampanhaPedidoResgatePagamentoID}
                carteiraId={props.CarteiraUsuarioInfo[0].CarteiraID || 0}
                loteId={selectedItem.CampanhaPedidoResgatePagamentoID}
              />
            </ModalContent>
          </Modal>

          {/* Modal Detalhes Pagamento */}
          <Modal open={openModal === 'detalhes-pagamento'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <PagedLoteItemDetalhe
                loteDetail={selectedItem}
                control={modalControl}
                take={5}
                loteId={selectedItem.CampanhaPedidoResgatePagamentoID}
                campanhaId={props.campanhaId}
              />
            </ModalContent>
          </Modal>

          {/* Modal Efetuar Pagamento */}
          <Modal open={openModal === 'efetuar-pagamento'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <ModalRealizarPagamento
                loteItem={selectedItem}
                onCancel={handleModalCancel}
                onModalClose={handleModalClose}
                campanhaId={props.campanhaId}
              />
            </ModalContent>
          </Modal>

          {/* Modal Baixar Notas */}
          <Modal open={openModal === 'baixar-notas'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Notas Fiscais</Typography>
                <Typography variant="body2">ID do Pagamento: {selectedItem.CampanhaPedidoResgatePagamentoID}</Typography>
                <Typography variant="body2">Lote: {selectedItem.Nome}</Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Baixar notas fiscais relacionadas a este pagamento.
                </Typography>
              </Box>
            </ModalContent>
          </Modal>

          {/* Modal Baixar Notas */}
          <Modal open={openModal === 'nota-repasse'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <Box>
                <ModalNotaRepasse
                  campanhaPedidoResgatePagamentoId={selectedItem.CampanhaPedidoResgatePagamentoID}
                  campanhaId={props.campanhaId}
                />
              </Box>
            </ModalContent>
          </Modal>

          {/* Modal Excluir */}
          <Modal open={openModal === 'excluir'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <ModalDeletarPagamento
                loteItem={selectedItem}
                onCancel={handleModalCancel}
                onConfirm={handleModalClose}
              />
            </ModalContent>
          </Modal>
        </>
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        statusOptions={statusOptions}
      />
    </Box>
  );
}
export default connector(PagedAprovar);